import React from 'react';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiButton from '@new-components/atoms/MuiButton';
import MuiTypography from '@new-components/atoms/MuiTypography';
import RangeInputs from '@new-components/atoms/RangeInputs';
import {SearchPriceFilterProps} from '@new-components/organisms/SearchPriceFilter';
import {SelectionType} from '@new-components/organisms/SearchPriceFilter';

type PriceFilterProps = SearchPriceFilterProps & {
  hasReset: boolean;
  label: string;
  value: SelectionType;
  handleChange: (selection: SelectionType) => void;
};
const PriceFilter = (props: PriceFilterProps) => {
  const [minPrice, setMinPrice] = React.useState<number | string | null>(
    props.value.min || null
  );
  const [maxPrice, setMaxPrice] = React.useState<number | string | null>(
    props.value.max || null
  );

  React.useEffect(() => {
    setMinPrice(props.value.min);
    setMaxPrice(props.value.max);
  }, [props.value.min, props.value.max]);

  React.useEffect(() => {
    props.handleChange({min: minPrice, max: maxPrice});
  }, [minPrice, maxPrice]);

  const handleMinInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.valueAsNumber;
    setMinPrice(isNaN(value) ? null : value);
  };
  const handleMaxInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.valueAsNumber;
    setMaxPrice(isNaN(value) ? null : value);
  };

  const handleMinBlur = () => {
    const minValue = Number(minPrice);
    const maxValue = Number(maxPrice);

    if (props.min && minValue <= props.min) {
      setMinPrice(props.min);
      return;
    } else if (props.max && props.step && minValue >= props.max) {
      setMinPrice(props.max - props.step);
      return;
    } else if (maxValue && minValue >= maxValue) {
      setMinPrice(maxValue - 1);
      return;
    }
  };

  const handleMaxBlur = () => {
    const minValue = Number(minPrice);
    const maxValue = Number(maxPrice);

    if (props.max && maxValue >= props.max) {
      setMaxPrice(props.max);
      return;
    } else if (props.min && props.step && maxValue <= props.min) {
      setMaxPrice(props.min + props.step);
      return;
    } else if (minValue && maxValue <= minValue) {
      setMaxPrice(minValue + 1);
      return;
    }
  };

  const handleReset = () => {
    setMinPrice(null);
    setMaxPrice(null);
  };

  return (
    <MuiBox>
      <MuiBox display='flex' alignItems='center' justifyContent='space-between'>
        <MuiTypography
          sx={{
            fontSize: props.hasReset ? '24px' : '17px',
            fontWeight: 500,
            letterSpacing: '-0.4px',
            color: props.hasReset ? '#2D2D2D' : '#000000',
            paddingBottom: '16px',
          }}>
          {props.label}
        </MuiTypography>
        {props.hasReset ? (
          <MuiButton
            variant='text'
            onClick={handleReset}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '17px',
              fontWeight: 400,
              lineHeight: '23.8px',
              textDecorationLine: 'underline',
              color: '#2D2D2D',
              ['&:hover']: {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              ['&:focus']: {
                outline: 'none',
              },
            }}>
            Reset
          </MuiButton>
        ) : null}
      </MuiBox>
      <RangeInputs
        suffix={props.currency}
        handleMaxInputChange={handleMaxInputChange}
        handleMinInputChange={handleMinInputChange}
        handleMinBlur={handleMinBlur}
        handleMaxBlur={handleMaxBlur}
        maxPlaceholder='Max Price'
        minPlaceholder='Min Price'
        maxValue={maxPrice as number}
        minValue={minPrice as number}
      />
    </MuiBox>
  );
};

export default PriceFilter;
